import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Building2, Layers, FolderKanban, FileText, ChevronRight, ArrowLeft,
  Loader2, AlertTriangle, BarChart3, Bell, MapPin, CheckCircle2,
  Calendar, Ruler, AlertCircle, Download, Activity, ImageOff,
} from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { useMe, useNavigation } from '../hooks/useMe';
import { useAdminClient, useAdminClients } from '../hooks/api/admin';
import { useNotifications } from '../hooks/api/notifications';
import {
  useInspectionTimestamps, useInspectionDetails, useInspectionAnalytics,
  getInspectionReportPdfUrl,
} from '../hooks/api/reports';
import { useAuthStore } from '../store/auth';

const PROJECT_IMAGES = [
  'https://images.unsplash.com/photo-1546998256-3529a72762b0?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
  'https://images.unsplash.com/photo-1669003153363-6d7ba8e20c7e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
  'https://images.unsplash.com/photo-1739706815629-06f3a2b6a1c4?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
  'https://images.unsplash.com/photo-1614366502473-e54666693b44?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
  'https://images.unsplash.com/photo-1672073311074-f60c4a5e7b92?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
  'https://images.unsplash.com/photo-1758465828635-cb04ee439095?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
];
function imageFor(id = '') {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return PROJECT_IMAGES[h % PROJECT_IMAGES.length];
}

const STATUS_COLORS = {
  active:    'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  trial:     'bg-blue-500/15 text-blue-300 border-blue-500/30',
  inactive:  'bg-slate-500/15 text-slate-300 border-slate-500/30',
  suspended: 'bg-red-500/15 text-red-300 border-red-500/30',
};

/* DDMMYYYY → "DD-MM-YYYY" for display */
function fmtTimestamp(ts, isoDate) {
  if (isoDate) {
    try { return new Date(isoDate).toISOString().slice(0, 10); } catch { /* */ }
  }
  if (typeof ts === 'string' && /^\d{8}$/.test(ts)) {
    return `${ts.slice(0, 2)}-${ts.slice(2, 4)}-${ts.slice(4)}`;
  }
  return ts;
}

/**
 * Hierarchical Dashboard. The same drill-down works for both:
 *   - super-admin (starts at Clients level)
 *   - client users (start auto-pinned to their client)
 *
 * Levels:  Clients → Industries → Projects → Project Detail → Deliverable Detail
 *          (clients level skipped when `pinnedToClient` is true)
 */
export function SuperAdminDashboard({
  onJumpToReports, onJumpToAnalytics, onJumpToAlerts,
  initialClientId = null,
  pinnedToClient = false,
}) {
  const me = useMe();
  const setActiveClientId = useAuthStore((s) => s.setActiveClientId);

  const [clientId, setClientId] = useState(initialClientId);
  const [industryId, setIndustryId] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [deliverableId, setDeliverableId] = useState(null);

  // Keep the activeClientId in sync the moment the user picks a client. The
  // axios interceptor sends X-Client-Id so every tenant-scoped endpoint
  // (notifications, dashboard overview, reports) targets the right tenant.
  useEffect(() => {
    if (clientId) setActiveClientId(clientId);
  }, [clientId, setActiveClientId]);

  const handleClientPick = (id) => {
    setClientId(id);
    setIndustryId(null);
    setProjectId(null);
    setDeliverableId(null);
  };

  // /admin/clients/{id} is super-admin-only — client users get 403. So we
  // only fetch the admin payload when the current user has access. For
  // pinned client users, the /auth/me/navigation tree is already scoped
  // server-side to projects enabled for them, so no extra filtering is
  // needed and we can just synthesise a minimal `clientDetail` from the
  // membership row in `me.clients`.
  const { data: adminClientDetail } = useAdminClient(pinnedToClient ? null : clientId);
  const myClientFromMe = (me?.clients || []).find((c) => c.id === clientId) || null;
  const clientDetail = pinnedToClient
    ? (myClientFromMe ? { ...myClientFromMe, project_ids: undefined } : null)
    : adminClientDetail;

  const navigation = useNavigation();
  const industries = navigation?.industries || [];

  // For super-admin the navigation tree contains every project, so we
  // narrow it down to what's enabled for the picked client. For pinned
  // client users we trust the server-filtered tree and pass it through.
  const enabledProjectIds = useMemo(
    () => new Set((adminClientDetail?.project_ids) || []),
    [adminClientDetail],
  );

  const filteredIndustries = useMemo(() => {
    if (!clientId) return industries;
    if (pinnedToClient) {
      // Server-side already filtered; just drop empty industries.
      return industries.filter((i) => (i.projects || []).length > 0);
    }
    if (!adminClientDetail) return [];   // still loading
    return industries
      .map((i) => ({ ...i, projects: (i.projects || []).filter((p) => enabledProjectIds.has(p.id)) }))
      .filter((i) => i.projects.length > 0);
  }, [industries, enabledProjectIds, clientId, pinnedToClient, adminClientDetail]);

  const selectedIndustry = filteredIndustries.find((i) => i.id === industryId);
  const projectsInIndustry = selectedIndustry?.projects || [];
  const selectedProject = projectsInIndustry.find((p) => p.id === projectId);
  const deliverablesInProject = selectedProject?.deliverables || [];
  const selectedDeliverable = deliverablesInProject.find((d) => d.id === deliverableId);

  const goHome      = () => { setClientId(pinnedToClient ? initialClientId : null);
                              setIndustryId(null); setProjectId(null); setDeliverableId(null); };
  const goToClient  = () => { setIndustryId(null); setProjectId(null); setDeliverableId(null); };
  const goToIndustry = () => { setProjectId(null); setDeliverableId(null); };
  const goToProject  = () => { setDeliverableId(null); };

  return (
    <div className="space-y-5">
      <Header
        me={me}
        pinnedToClient={pinnedToClient}
        clientLabel={pinnedToClient ? null : clientDetail?.name}
        clientCode={pinnedToClient ? null : clientDetail?.code}
        industryLabel={selectedIndustry?.name}
        industryCode={selectedIndustry?.code}
        projectLabel={selectedProject?.name}
        projectCode={selectedProject?.code}
        deliverableLabel={selectedDeliverable?.name}
        deliverableCode={selectedDeliverable?.code}
        onHome={goHome}
        onClient={goToClient}
        onIndustry={goToIndustry}
        onProject={goToProject}
      />

      {!clientId && !pinnedToClient && (
        <ClientsLevel onPick={handleClientPick} />
      )}
      {clientId && !industryId && (
        <IndustriesLevel
          industries={filteredIndustries}
          // Loading: client-users wait on the navigation tree;
          // super-admin waits on the per-client admin payload.
          isLoading={pinnedToClient ? !navigation : !adminClientDetail}
          onPick={setIndustryId}
        />
      )}
      {clientId && industryId && !projectId && (
        <ProjectsLevel
          projects={projectsInIndustry}
          industryName={selectedIndustry?.name}
          onPick={setProjectId}
        />
      )}
      {clientId && industryId && projectId && !deliverableId && (
        <ProjectDetailsLevel
          client={clientDetail}
          industry={selectedIndustry}
          project={selectedProject}
          onDeliverableClick={setDeliverableId}
        />
      )}
      {clientId && industryId && projectId && deliverableId && (
        <DeliverableDetailsLevel
          client={clientDetail}
          industry={selectedIndustry}
          project={selectedProject}
          deliverable={selectedDeliverable}
          onJumpToReports={onJumpToReports}
          onJumpToAnalytics={onJumpToAnalytics}
          onJumpToAlerts={onJumpToAlerts}
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Header / Breadcrumb                                                         */
/* -------------------------------------------------------------------------- */
function Header({
  me, pinnedToClient, clientLabel, clientCode,
  industryLabel, industryCode, projectLabel, projectCode,
  deliverableLabel, deliverableCode,
  onHome, onClient, onIndustry, onProject,
}) {
  return (
    <div className="space-y-2">
      <div>
        <h1 className="text-2xl font-semibold">Dashboard</h1>
        <p className="text-sm text-muted-foreground">
          Welcome back, {me?.full_name || 'there'}. Drill into any
          {pinnedToClient ? ' industry → project → deliverable' : ' client → industry → project → deliverable'}
          {' '}to see live reports, analytics, and alerts.
        </p>
      </div>
      <Breadcrumb
        pinnedToClient={pinnedToClient}
        clientLabel={clientLabel} clientCode={clientCode}
        industryLabel={industryLabel} industryCode={industryCode}
        projectLabel={projectLabel} projectCode={projectCode}
        deliverableLabel={deliverableLabel} deliverableCode={deliverableCode}
        onHome={onHome} onClient={onClient} onIndustry={onIndustry} onProject={onProject}
      />
    </div>
  );
}

function Breadcrumb({
  pinnedToClient,
  clientLabel, clientCode, industryLabel, industryCode,
  projectLabel, projectCode, deliverableLabel, deliverableCode,
  onHome, onClient, onIndustry, onProject,
}) {
  const Crumb = ({ icon: Icon, label, code, onClick, active }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={
        'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm transition ' +
        (active
          ? 'bg-sky-500/10 text-sky-300 cursor-default'
          : 'text-muted-foreground hover:bg-white/5 hover:text-foreground')
      }
    >
      {Icon && <Icon className="w-3.5 h-3.5" />}
      <span>{label}</span>
      {code && <span className="text-[10px] font-mono opacity-70">({code})</span>}
    </button>
  );

  return (
    <div className="flex items-center gap-1 flex-wrap">
      {!pinnedToClient && (
        <Crumb icon={Building2} label="All Clients" onClick={onHome} active={!clientLabel} />
      )}
      {clientLabel && (
        <>
          {!pinnedToClient && <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/60" />}
          <Crumb icon={Building2} label={clientLabel} code={clientCode}
                 onClick={industryLabel ? onClient : null} active={!industryLabel} />
        </>
      )}
      {pinnedToClient && !industryLabel && (
        <Crumb icon={Layers} label="Industries" active />
      )}
      {industryLabel && (
        <>
          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/60" />
          <Crumb icon={Layers} label={industryLabel} code={industryCode}
                 onClick={projectLabel ? onIndustry : null} active={!projectLabel} />
        </>
      )}
      {projectLabel && (
        <>
          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/60" />
          <Crumb icon={FolderKanban} label={projectLabel} code={projectCode}
                 onClick={deliverableLabel ? onProject : null} active={!deliverableLabel} />
        </>
      )}
      {deliverableLabel && (
        <>
          <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/60" />
          <Crumb icon={CheckCircle2} label={deliverableLabel} code={deliverableCode} active />
        </>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Level 1 — Clients (super-admin only)                                       */
/* -------------------------------------------------------------------------- */
function ClientsLevel({ onPick }) {
  const { data, isLoading } = useAdminClients({ page_size: 200 });
  const clients = data?.data || [];

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-12 justify-center text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading clients…
      </div>
    );
  }
  if (clients.length === 0) {
    return (
      <Card className="border-0 bg-card">
        <CardContent className="py-16 text-center text-sm text-muted-foreground">
          No clients onboarded yet. Use the Clients page to provision one.
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-3">
      <SectionTitle accent="bg-blue-600" label="Clients" count={clients.length} />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {clients.map((c) => (
          <ClientCard key={c.id} c={c} onPick={() => onPick(c.id)} />
        ))}
      </div>
    </div>
  );
}

function ClientCard({ c, onPick }) {
  return (
    <button type="button" onClick={onPick} className="text-left group block w-full">
      <Card className="border-white/5 bg-white/5 backdrop-blur-md hover:bg-white/10 transition-all duration-200 group-hover:border-sky-500/40 h-24 overflow-hidden">
        <CardContent className="p-4 flex items-center gap-4 h-full">
          <ClientLogo client={c} size="lg" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h4 className="font-semibold text-base text-white truncate">{c.name}</h4>
              <span className="text-[10px] font-mono text-white/60 shrink-0">{c.code}</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] border ${STATUS_COLORS[c.status] || ''}`}>
                {c.status}
              </span>
              {c.contract_tier && (
                <Badge variant="outline" className="text-[10px] capitalize">{c.contract_tier}</Badge>
              )}
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground shrink-0" />
        </CardContent>
      </Card>
    </button>
  );
}

function ClientLogo({ client, size = 'md' }) {
  const [broken, setBroken] = React.useState(false);
  const px = size === 'lg' ? 56 : 40;
  const iconDim = size === 'lg' ? 'w-7 h-7' : 'w-5 h-5';
  const url = client?.logo_url;
  const wrapperStyle = { width: px, height: px, minWidth: px, flex: '0 0 auto' };
  const wrapperClass = 'rounded-xl bg-white/10 flex items-center justify-center border border-white/10 overflow-hidden';
  if (!url || broken) {
    return (
      <div className={wrapperClass} style={wrapperStyle}>
        <Building2 className={`${iconDim} text-sky-300`} />
      </div>
    );
  }
  return (
    <div className={wrapperClass} style={wrapperStyle}>
      <img
        src={url}
        alt={`${client.name || client.code} logo`}
        width={px}
        height={px}
        style={{ width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%' }}
        className="block object-contain"
        onError={() => setBroken(true)}
        loading="lazy"
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Level 2 — Industries                                                        */
/* -------------------------------------------------------------------------- */
function IndustriesLevel({ industries, isLoading, onPick }) {
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-12 justify-center text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading industries…
      </div>
    );
  }
  if (industries.length === 0) {
    return (
      <Card className="border-0 bg-card">
        <CardContent className="py-16 text-center text-sm text-muted-foreground">
          No industries with enabled projects yet.
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-3">
      <SectionTitle accent="bg-purple-600" label="Industries" count={industries.length} />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {industries.map((i) => (
          <HeroCard
            key={i.id}
            onClick={() => onPick(i.id)}
            imageUrl={i.image_url}
            fallbackId={i.id}
            title={i.name}
            code={i.code}
            subtitle={`${i.projects.length} project${i.projects.length === 1 ? '' : 's'}`}
            subtitleIcon={FolderKanban}
            // No progress bar at industry level — leave undefined.
          />
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Level 3 — Projects                                                          */
/* -------------------------------------------------------------------------- */
function ProjectsLevel({ projects, industryName, onPick }) {
  if (!projects.length) {
    return (
      <Card className="border-0 bg-card">
        <CardContent className="py-16 text-center text-sm text-muted-foreground">
          No projects under {industryName} for this client.
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="space-y-3">
      <SectionTitle accent="bg-sky-500" label="Projects" count={projects.length} />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {projects.map((p) => (
          <HeroCard
            key={p.id}
            onClick={() => onPick(p.id)}
            imageUrl={p.image_url}
            fallbackId={p.id}
            title={p.name}
            code={p.code}
            subtitle={p.location || industryName}
            subtitleIcon={MapPin}
            progress={typeof p.progress === 'number' ? p.progress : undefined}
          />
        ))}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Level 4 — Project Details (deliverables grid + jump links + alerts)         */
/* -------------------------------------------------------------------------- */
function ProjectDetailsLevel({
  client, industry, project, onDeliverableClick,
}) {
  // No hero / action buttons here — those live one level deeper, on
  // the Deliverable Details page where they have an inspection date in
  // scope. This level is a clean deliverable picker.
  const { data: notifPage } = useNotifications({ status: 'unread', page_size: 5 });
  const alerts = notifPage?.data || [];
  const dels = project?.deliverables || [];

  return (
    <div className="space-y-5">
      <div className="space-y-3">
        <SectionTitle accent="bg-emerald-500" label="Deliverables" count={dels.length} />
        {dels.length === 0 ? (
          <Card className="border-0 bg-card">
            <CardContent className="py-10 text-center text-sm text-muted-foreground">
              No deliverables under this project yet. Add them from Clients → Project Catalog.
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {dels.map((d) => (
              <HeroCard
                key={d.id}
                onClick={() => onDeliverableClick?.(d.id)}
                imageUrl={project?.image_url}
                fallbackId={d.id}
                title={d.name}
                code={d.code}
                subtitle={project?.name}
                subtitleIcon={FolderKanban}
                // No real progress yet at deliverable level — left blank.
              />
            ))}
          </div>
        )}
      </div>

      <div className="space-y-3">
        <SectionTitle accent="bg-orange-500" label="Recent Alerts" count={alerts.length} />
        {alerts.length === 0 ? (
          <Card className="border-0 bg-card">
            <CardContent className="py-8 text-center text-sm text-muted-foreground">
              No unread alerts for this client.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {alerts.map((n) => <AlertRow key={n.id} n={n} />)}
          </div>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Level 5 — Deliverable Details (the rich Figma-style view)                   */
/* -------------------------------------------------------------------------- */
function DeliverableDetailsLevel({
  client, industry, project, deliverable,
  onJumpToReports, onJumpToAnalytics, onJumpToAlerts,
}) {
  const ctx = useMemo(() => ({
    industry_id: industry?.id,
    project_id:  project?.id,
    deliverable_id: deliverable?.id,
  }), [industry, project, deliverable]);

  const ready = Boolean(ctx.industry_id && ctx.project_id && ctx.deliverable_id);
  const tsQuery = useInspectionTimestamps(ctx, { enabled: ready });
  const items = tsQuery.data?.items || [];

  // Selected inspection date — defaults to the latest. The dropdown above
  // the hero lets the user switch versions; everything below re-fetches.
  const [selectedTs, setSelectedTs] = useState(null);
  // Reset selection when the deliverable changes; re-default to latest
  // once timestamps load.
  useEffect(() => { setSelectedTs(null); }, [deliverable?.id]);
  useEffect(() => {
    if (selectedTs) return;
    if (items.length > 0) setSelectedTs(items[0].timestamp);
  }, [items, selectedTs]);

  const activeItem = items.find((it) => it.timestamp === selectedTs) || items[0];
  const activeTs = activeItem?.timestamp;

  const detailsQuery = useInspectionDetails(activeTs, ctx, { enabled: Boolean(activeTs) });
  const analyticsQuery = useInspectionAnalytics(activeTs, ctx, { enabled: Boolean(activeTs) });
  const reportsJson = detailsQuery.data?.data;
  const analyticsJson = analyticsQuery.data?.data;

  const { data: notifPage } = useNotifications({ page_size: 5 });
  const recent = notifPage?.data || [];

  const summary = useMemo(() => extractSummary(reportsJson), [reportsJson]);
  const arrayCount = useMemo(() => countArrayItems(reportsJson), [reportsJson]);
  const heroImage = summary.hero_image_url;
  const metrics = useMemo(
    () => buildMetrics(analyticsJson, arrayCount, activeItem),
    [analyticsJson, arrayCount, activeItem],
  );

  const [downloading, setDownloading] = useState(false);
  const handleDownloadReport = async () => {
    if (!activeTs || !ready) return;
    try {
      setDownloading(true);
      const r = await getInspectionReportPdfUrl(activeTs, ctx);
      if (r?.url) window.open(r.url, '_blank', 'noopener,noreferrer');
    } catch (e) { /* swallow — user can also use Reports tab */ }
    finally { setDownloading(false); }
  };

  return (
    <div className="space-y-5">
      {/* Inspection-date picker — every dropdown change re-fetches the
          reports.json + analytics.json + report-pdf for that timestamp,
          and the hero / stats / metrics below update accordingly. */}
      <Card className="border-0 bg-card">
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="w-4 h-4" /> Inspection date
          </div>
          <div className="min-w-[220px]">
            <Select
              value={activeTs || ''}
              onValueChange={(v) => setSelectedTs(v)}
              disabled={!ready || tsQuery.isLoading || items.length === 0}
            >
              <SelectTrigger>
                <SelectValue placeholder={
                  tsQuery.isLoading ? 'Loading…'
                    : items.length === 0 ? 'No inspections yet'
                    : 'Pick a date'
                } />
              </SelectTrigger>
              <SelectContent>
                {items.map((it) => (
                  <SelectItem key={it.timestamp} value={it.timestamp}>
                    {fmtTimestamp(it.timestamp, it.date)}
                    {it.has_pdf ? '' : ' · no PDF'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {items.length > 0 && (
            <span className="text-xs text-muted-foreground">
              {items.length} inspection{items.length === 1 ? '' : 's'} available
            </span>
          )}
        </CardContent>
      </Card>

      {/* Hero — overview image of the selected inspection */}
      <Card className="border-0 bg-card overflow-hidden">
        <CardContent className="p-0">
          <div className="relative h-56 bg-muted/30">
            {heroImage ? (
              <img src={heroImage} alt={deliverable?.name}
                   className="w-full h-full object-cover" />
            ) : (
              <ProjectHeroImage url={project?.image_url}
                                fallbackId={deliverable?.id || project?.id}
                                alt={deliverable?.name} />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-5">
              <div className="flex items-center gap-2 mb-1.5">
                <Badge variant="outline" className="bg-black/40 text-white border-white/30 backdrop-blur-sm text-[10px] font-mono">{deliverable?.code}</Badge>
                <Badge variant="outline" className="bg-black/40 text-white border-white/30 backdrop-blur-sm text-[10px]">{project?.name}</Badge>
                <Badge variant="outline" className="bg-black/40 text-white border-white/30 backdrop-blur-sm text-[10px]">{industry?.name}</Badge>
                {activeItem && (
                  <Badge variant="outline" className="bg-black/40 text-white border-white/30 backdrop-blur-sm text-[10px] font-mono">
                    {fmtTimestamp(activeItem.timestamp, activeItem.date)}
                  </Badge>
                )}
              </div>
              <h3 className="text-2xl font-semibold text-white">{deliverable?.name}</h3>
            </div>
          </div>

          {/* Top stats row */}
          <div className="p-5 pt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 border-t border-border/40">
            <StatTile icon={MapPin}    iconColor="text-sky-300"     bg="bg-sky-500/10"     label="Location"           value={summary.location || '—'} />
            <StatTile icon={Calendar}  iconColor="text-emerald-300" bg="bg-emerald-500/10" label="Inspected Date"     value={summary.inspected_date || (activeItem ? fmtTimestamp(activeItem.timestamp, activeItem.date) : '—')} />
            <StatTile icon={Ruler}     iconColor="text-purple-300"  bg="bg-purple-500/10"  label="Total Distance"     value={summary.total_perimeter || '—'} />
            <StatTile icon={AlertCircle} iconColor="text-orange-300" bg="bg-orange-500/10" label="Defects Identified" value={(summary.encroached_spots ?? arrayCount ?? '—') + (typeof (summary.encroached_spots ?? arrayCount) === 'number' ? ' defects' : '')} />
          </div>

          {/* Action buttons row */}
          <div className="p-5 pt-0 grid grid-cols-2 md:grid-cols-4 gap-3">
            <ActionTile icon={FileText}  label="Reports"          onClick={onJumpToReports} />
            <ActionTile icon={Bell}      label="Alerts"           onClick={onJumpToAlerts} />
            <ActionTile icon={BarChart3} label="Analytics"        onClick={onJumpToAnalytics} />
            <ActionTile icon={Download}  label={downloading ? 'Opening…' : 'Download Report'} accent
                        onClick={handleDownloadReport} disabled={!activeItem?.has_pdf || downloading} />
          </div>
        </CardContent>
      </Card>

      {/* Project Metrics + Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="space-y-3">
          <SectionTitle accent="bg-sky-500" label="Project Metrics" />
          {metrics.length === 0 ? (
            <Card className="border-0 bg-card">
              <CardContent className="py-8 text-center text-xs text-muted-foreground">
                {tsQuery.isLoading || detailsQuery.isLoading || analyticsQuery.isLoading
                  ? 'Loading metrics…'
                  : 'No metrics available — submit an inspection from Annotator Upload.'}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {metrics.map((m, i) => (
                <Card key={i} className="border-0 bg-card">
                  <CardContent className="p-4 flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">{m.label}</span>
                    <span className={`text-base font-semibold ${m.color || ''}`}>{m.value}</span>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-3">
          <SectionTitle accent="bg-emerald-500" label="Recent Activity" count={recent.length} />
          {recent.length === 0 ? (
            <Card className="border-0 bg-card">
              <CardContent className="py-8 text-center text-xs text-muted-foreground">
                No activity yet for this client.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {recent.map((n) => <AlertRow key={n.id} n={n} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---- Small building blocks for Level 5 ---- */
function StatTile({ icon: Icon, iconColor, bg, label, value }) {
  return (
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg ${bg} flex items-center justify-center shrink-0`}>
        <Icon className={`w-5 h-5 ${iconColor}`} />
      </div>
      <div className="min-w-0">
        <div className="text-xs text-muted-foreground">{label}</div>
        <div className="text-base font-semibold truncate">{value}</div>
      </div>
    </div>
  );
}

function ActionTile({ icon: Icon, label, onClick, accent = false, disabled = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={
        'rounded-lg border p-4 flex flex-col items-center justify-center gap-2 transition ' +
        (accent
          ? 'bg-sky-500 hover:bg-sky-600 border-sky-500 text-white disabled:opacity-50 disabled:hover:bg-sky-500'
          : 'border-border bg-card hover:bg-white/5 text-foreground disabled:opacity-50')
      }
    >
      <Icon className="w-5 h-5" />
      <span className="text-sm font-medium">{label}</span>
    </button>
  );
}

function AlertRow({ n }) {
  const colors = n.type === 'critical'
    ? 'bg-red-500/15 border-red-500/30 text-red-300'
    : n.type === 'warning'
    ? 'bg-amber-500/15 border-amber-500/30 text-amber-300'
    : 'bg-sky-500/15 border-sky-500/30 text-sky-300';
  return (
    <Card className="border-border bg-card">
      <CardContent className="p-4 flex items-start gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 border ${colors}`}>
          <AlertTriangle className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm">{n.title}</div>
          <div className="text-xs text-muted-foreground line-clamp-2">{n.body}</div>
        </div>
        <Badge variant="outline" className="text-[10px] uppercase shrink-0">{n.type}</Badge>
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Heuristics for open-ended reports.json + analytics.json                    */
/* -------------------------------------------------------------------------- */
function extractSummary(reportsJson) {
  if (!reportsJson || typeof reportsJson !== 'object') return {};
  // Walk the first object section to find common keys.
  const out = {};
  for (const v of Object.values(reportsJson)) {
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const lc = (k) => k.toLowerCase();
      for (const [k, val] of Object.entries(v)) {
        const key = lc(k);
        if (typeof val !== 'string' && typeof val !== 'number') continue;
        if (!out.location && (key === 'location')) out.location = String(val);
        if (!out.inspected_date && (key === 'inspecteddate' || key === 'inspected_date'))
          out.inspected_date = String(val);
        if (!out.total_perimeter && (key === 'totalperimeter' || key === 'total_perimeter'))
          out.total_perimeter = String(val);
        if (!out.total_area && (key === 'totalarea' || key === 'total_area'))
          out.total_area = String(val);
        if (out.encroached_spots == null
            && (key === 'totalencroachedspots' || key === 'total_encroached_spots'))
          out.encroached_spots = Number(val);
      }
      if (!out.hero_image_url && typeof v.image_path === 'string'
          && /^https?:\/\//.test(v.image_path)) {
        out.hero_image_url = v.image_path;
      }
      if (out.location || out.inspected_date) break;  // first matching section wins
    }
  }
  return out;
}

function countArrayItems(reportsJson) {
  if (!reportsJson || typeof reportsJson !== 'object') return 0;
  let total = 0;
  for (const v of Object.values(reportsJson)) {
    if (Array.isArray(v)) total += v.length;
  }
  return total || null;
}

function buildMetrics(analyticsJson, arrayCount, latest) {
  const out = [];
  if (analyticsJson && typeof analyticsJson === 'object') {
    for (const [k, v] of Object.entries(analyticsJson)) {
      if (k.startsWith('graphType_')) continue;
      if (Array.isArray(v) && v.length > 0) {
        const total = v.reduce((acc, d) => acc + Number(d.value ?? d.count ?? 0), 0);
        if (total > 0) {
          out.push({ label: prettyKey(k), value: String(total) });
        }
      }
    }
  }
  if (typeof arrayCount === 'number' && arrayCount > 0) {
    out.push({ label: 'Reported Areas', value: String(arrayCount) });
  }
  if (latest?.timestamp) {
    out.push({ label: 'Latest Inspection', value: fmtTimestamp(latest.timestamp, latest.date) });
  }
  return out;
}

function prettyKey(k) {
  return String(k)
    .replace(/^[a-z]/, (m) => m.toUpperCase())
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ');
}

/* -------------------------------------------------------------------------- */
/* Misc                                                                       */
/* -------------------------------------------------------------------------- */
function SectionTitle({ accent = 'bg-sky-500', label, count }) {
  return (
    <div className="flex items-center gap-3">
      <div className={`w-1 h-6 ${accent} rounded-full`} />
      <h2 className="text-xl font-bold text-foreground">{label}</h2>
      {typeof count === 'number' && (
        <Badge variant="outline" className="text-[10px]">{count}</Badge>
      )}
    </div>
  );
}

/**
 * Reusable card for the drill-down levels — full-bleed hero image,
 * gradient overlay, code badge, title, subtitle (with optional icon)
 * and an optional progress bar. Same look + feel for industries,
 * projects, and deliverables so the whole drill-down reads as one design.
 *
 * Layout:
 *   ┌────────────────────────────────────┐
 *   │       (background image)            │
 *   │                                     │
 *   │  [code]                             │
 *   │  TITLE                              │
 *   │  📍 subtitle                        │
 *   │  Progress              87% ▓▓▓▓░░░  │
 *   └────────────────────────────────────┘
 */
function HeroCard({
  onClick, imageUrl, fallbackId, title, code, subtitle,
  subtitleIcon: SubIcon, progress, progressLabel = 'Progress',
}) {
  const showProgress = typeof progress === 'number';
  const pct = showProgress ? Math.max(0, Math.min(100, Math.round(progress))) : 0;
  return (
    <button type="button" onClick={onClick} className="text-left group block w-full">
      <Card className="border-border bg-card hover:shadow-lg transition-all duration-300 hover:scale-[1.01] overflow-hidden h-52">
        <CardContent className="p-0 relative h-full">
          <ProjectHeroImage url={imageUrl} fallbackId={fallbackId} alt={title} />
          {/* Gradient so the bottom text stays legible on any image. */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/45 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-4 space-y-2 flex flex-col gap-1">
            {code && (
              <Badge variant="outline" className="self-start bg-black/40 text-white border-white/30 backdrop-blur-sm text-[10px] font-mono">
                {code}
              </Badge>
            )}
            <h4 className="font-semibold text-white text-xl leading-tight line-clamp-1">{title}</h4>
            {subtitle && (
              <div className="flex items-center text-xs text-white/85 gap-1.5">
                {SubIcon && <SubIcon className="w-3.5 h-3.5" />}
                <span className="truncate">{subtitle}</span>
              </div>
            )}
            {showProgress && (
              <div className="pt-1">
                <div className="flex items-center justify-between text-[11px] text-white/80">
                  <span>{progressLabel}</span>
                  <span className="font-medium">{pct}%</span>
                </div>
                <div className="mt-1 h-1.5 w-full rounded-full bg-white/15 overflow-hidden">
                  <div
                    className="h-full bg-sky-400 rounded-full"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </button>
  );
}

/**
 * Compact branded image for industry-row cards. Pixel-pinned wrapper +
 * onError fallback to a coloured icon tile.
 */
function BrandImage({
  url, alt, size = 48,
  bg = 'bg-purple-500/10', borderColor = 'border-purple-500/20',
  fallback,
}) {
  const [broken, setBroken] = useState(false);
  const wrapperStyle = { width: size, height: size, minWidth: size, flex: '0 0 auto' };
  const wrapperClass = `${bg} rounded-xl flex items-center justify-center border ${borderColor} overflow-hidden`;
  if (!url || broken) {
    return <div className={wrapperClass} style={wrapperStyle}>{fallback}</div>;
  }
  return (
    <div className={wrapperClass} style={wrapperStyle}>
      <img
        src={url}
        alt={alt || ''}
        width={size}
        height={size}
        style={{ width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%' }}
        className="block object-contain"
        onError={() => setBroken(true)}
        loading="lazy"
      />
    </div>
  );
}

/**
 * Full-bleed project hero image. Tries the S3-presigned URL first; on
 * error or when the URL isn't set, falls back to a deterministic stock
 * thumbnail keyed by `fallbackId`.
 */
function ProjectHeroImage({ url, fallbackId, alt }) {
  const [broken, setBroken] = useState(false);
  if (url && !broken) {
    return (
      <img
        src={url}
        alt={alt || ''}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        onError={() => setBroken(true)}
        loading="lazy"
      />
    );
  }
  return (
    <ImageWithFallback
      src={imageFor(fallbackId)}
      alt={alt || ''}
      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
    />
  );
}
